import { useEffect, useState } from "react";
import { useAuth } from "../auth/context";

// Notification controls — gated on the app being installed (running in
// standalone display mode).  iOS Safari only allows Web Push from installed
// PWAs, and even on Chromium browsers we want to avoid prompting in a
// regular tab where notifications are easy to lose track of.
//
// States the button cycles through:
//   - "installed" but no permission yet → "Enable notifications"
//   - permission granted + subscribed   → "Test" / "Disable"
//   - permission denied                 → "Notifications blocked" hint
//   - not installed                     → button is hidden entirely
//   - VAPID not configured server-side  → button is hidden

type Mode = "hidden" | "enable" | "enabled" | "blocked" | "loading";

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  if (window.matchMedia?.("(display-mode: standalone)").matches) return true;
  // iOS Safari: window.navigator.standalone is true when launched from
  // the home screen.  Not standard, hence the cast.
  return Boolean((window.navigator as { standalone?: boolean }).standalone);
}

function urlBase64ToBuffer(base64: string): ArrayBuffer {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const buf = new ArrayBuffer(raw.length);
  const view = new Uint8Array(buf);
  for (let i = 0; i < raw.length; i++) view[i] = raw.charCodeAt(i);
  return buf;
}

export function NotificationButton() {
  const { state } = useAuth();
  const accessToken =
    (state.status === "authenticated" && state.session.access_token) ||
    (state.status === "offline" && state.session.access_token) ||
    null;

  const [mode, setMode] = useState<Mode>("loading");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initial state computation: only show if installed + permissions allow.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isStandalone()) {
        setMode("hidden");
        return;
      }
      if (
        !("Notification" in window) ||
        !("serviceWorker" in navigator) ||
        !("PushManager" in window)
      ) {
        setMode("hidden");
        return;
      }
      // Check that the server actually has VAPID keys.  No keys → hide.
      try {
        const r = await fetch("/api/push/public-key");
        if (!r.ok) {
          setMode("hidden");
          return;
        }
      } catch {
        setMode("hidden");
        return;
      }
      if (Notification.permission === "denied") {
        if (!cancelled) setMode("blocked");
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub && Notification.permission === "granted") {
        if (!cancelled) setMode("enabled");
      } else {
        if (!cancelled) setMode("enable");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const enable = async () => {
    if (!accessToken) return;
    setBusy(true);
    setError(null);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        setMode(perm === "denied" ? "blocked" : "enable");
        return;
      }
      const keyRes = await fetch("/api/push/public-key");
      if (!keyRes.ok) throw new Error(`public-key ${keyRes.status}`);
      const { publicKey } = (await keyRes.json()) as { publicKey: string };

      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToBuffer(publicKey),
      });
      const json = sub.toJSON() as {
        endpoint: string;
        keys?: { p256dh?: string; auth?: string };
      };
      const body = {
        endpoint: json.endpoint,
        keys: { p256dh: json.keys?.p256dh ?? "", auth: json.keys?.auth ?? "" },
        userAgent: navigator.userAgent,
      };
      const r = await fetch("/api/push/subscribe", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error(`subscribe ${r.status}`);
      setMode("enabled");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    if (!accessToken) return;
    setBusy(true);
    setError(null);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await fetch("/api/push/unsubscribe", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ endpoint: sub.endpoint }),
        }).catch(() => {});
        await sub.unsubscribe();
      }
      setMode("enable");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    if (!accessToken) return;
    setBusy(true);
    setError(null);
    try {
      const r = await fetch("/api/push/test", {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!r.ok) throw new Error(`test ${r.status}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  if (mode === "hidden" || mode === "loading") return null;

  if (mode === "blocked") {
    return (
      <span
        className="text-xs text-slate-500 dark:text-slate-400"
        title="Permission has been denied. Re-enable in your browser's site settings."
      >
        🔕 Notifications blocked
      </span>
    );
  }

  if (mode === "enable") {
    return (
      <button
        type="button"
        onClick={() => void enable()}
        disabled={busy}
        className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
        title="Enable push notifications on this installed device"
      >
        {busy ? "Enabling…" : "Enable notifications"}
      </button>
    );
  }

  return (
    <span className="inline-flex items-center gap-1">
      <button
        type="button"
        onClick={() => void test()}
        disabled={busy}
        className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
      >
        {busy ? "…" : "Test 🔔"}
      </button>
      <button
        type="button"
        onClick={() => void disable()}
        disabled={busy}
        className="rounded-md px-1 text-xs text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100"
        title="Stop receiving notifications on this device"
      >
        ✕
      </button>
      {error && <span className="text-xs text-rose-600 dark:text-rose-400">{error}</span>}
    </span>
  );
}
